import { useEffect, useRef } from "react";

/**
 * Polls `fn` every `intervalMs` while document.visibilityState === "visible".
 * fn is called once immediately if `immediate` is true.
 * Deps behave like useEffect deps — poll restarts when they change.
 */
export function useLivePoll(fn, intervalMs = 15000, deps = [], { immediate = true } = {}) {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    let cancelled = false;
    let handle = null;

    const tick = async () => {
      if (cancelled) return;
      if (document.visibilityState === "visible") {
        try { await fnRef.current(); } catch (_) {}
      }
    };
    const start = () => {
      stop();
      handle = setInterval(tick, intervalMs);
    };
    const stop = () => {
      if (handle) { clearInterval(handle); handle = null; }
    };
    const onVis = () => {
      if (document.visibilityState === "visible") { tick(); start(); }
      else stop();
    };

    if (immediate) tick();
    start();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      stop();
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs, ...deps]);
}
